import React, { useCallback, useRef } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import TreeSelectItem from '../tree-select-item';
import TreeSelectGroupLabel from '../tree-select-group-label';
import TreeSelectNoResult from '../tree-select-no-result';
import Divider from '../../../divider';
import { flatRowTypes, SELECT_ALL_ID, itemCheckStates } from '../../constants';
import { useTreeSelectContext } from '../../context/tree-select-context';
import keyNames from '../../../../constants/key-names';

const TreeSelectList = () => {
  const {
    flatRows,
    noResultsText,
    listRef,
    inputRef,
    isOpen,
    activeId,
    isFirstRow,
    selectAllState,
    idToItem,
    itemAncestors,
    checkStateMap,
    expandedIds,
    navigateToNext,
    navigateToPrev,
    setActiveId,
    onSelectAllToggle,
    onItemToggle,
    onExpandToggle,
  } = useTreeSelectContext();

  const innerRef = useRef(null);
  const mergedRef = useMergeRefs([listRef, innerRef]);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!isOpen || !activeId) return;

      if (e.key === keyNames.DOWN) {
        e.preventDefault();
        navigateToNext();
        return;
      }

      if (e.key === keyNames.UP) {
        e.preventDefault();
        if (isFirstRow) {
          inputRef.current?.focus();
          setActiveId(null);
        } else {
          navigateToPrev();
        }
        return;
      }

      const item = idToItem.get(activeId);

      if (e.key === keyNames.ENTER || e.key === keyNames.SPACE) {
        e.preventDefault();
        if (activeId === SELECT_ALL_ID) {
          onSelectAllToggle(selectAllState !== itemCheckStates.CHECKED);
          return;
        }

        if (!item) {
          return;
        }
        const checkState = checkStateMap.get(activeId) ?? itemCheckStates.UNCHECKED;
        onItemToggle(item, checkState !== itemCheckStates.CHECKED);
        return;
      }

      if (e.key === keyNames.RIGHT) {
        e.preventDefault();
        if (!item?.disabled && item?.items?.length) {
          if (expandedIds.has(activeId)) {
            navigateToNext();
          } else {
            onExpandToggle(activeId);
          }
        }
        return;
      }

      if (e.key === keyNames.LEFT) {
        e.preventDefault();
        if (!item?.disabled && item?.items?.length && expandedIds.has(activeId)) {
          onExpandToggle(activeId);
          return;
        }
        const parentId = itemAncestors.get(activeId)?.[0];
        if (parentId) {
          setActiveId(parentId);
        }
      }
    },
    [
      isOpen,
      activeId,
      isFirstRow,
      navigateToNext,
      navigateToPrev,
      setActiveId,
      onSelectAllToggle,
      selectAllState,
      idToItem,
      itemAncestors,
      checkStateMap,
      onItemToggle,
      expandedIds,
      onExpandToggle,
      inputRef,
    ]
  );

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    innerRef.current?.focus();
  }, []);

  return (
    <div
      ref={mergedRef}
      className="ws-ui-tree-select__list"
      role="listbox"
      aria-multiselectable="true"
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      onKeyDown={handleListKeyDown}
    >
      {flatRows.length === 0 ? (
        <TreeSelectNoResult>{noResultsText}</TreeSelectNoResult>
      ) : (
        flatRows.map((row) => {
          if (row.type === flatRowTypes.DIVIDER) {
            return <Divider key={row.id} />;
          }

          if (row.type === flatRowTypes.GROUP_LABEL) {
            return <TreeSelectGroupLabel key={row.id} label={row.label} depth={row.depth} />;
          }

          return <TreeSelectItem key={row.id} item={row.item} depth={row.depth} />;
        })
      )}
    </div>
  );
};

export default TreeSelectList;
